import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";

// Types
import { ICitActivity, ICITBase, ICitExportHistoryEntries } from "@/Common/types/ContractsInTransit/model";

// APIs
import { getCITSystemSettings } from "@DataPages/api/financeManagement/cit/queries";

// Constants
const DMS_FUNDED_SOURCES = ["Cash Receipts", "Manual Entries", "Cash Disbursements"];

const ACTIVITY_TYPES = {
	FinanceReserve: "Finance Reserve",
	CIT: "CIT",
};

interface ICitContext extends Omit<ICitContextProviderProps, "children" | "administrationToolsDmsSources"> {
	filteredReceivables: string[] | null;
	activityType?: string;
	setFilteredReceivables: (value: string[] | null) => void;
	isFunded: (receivable: ICitActivity) => boolean;
	isFundable: (receivable: ICitActivity) => boolean;
	isUnfunded: (receivable: ICitActivity) => boolean;
	fundedSources: string[];
}

const CitContext = createContext<ICitContext>({
	receivables: [],
	filteredReceivables: null,
	activityType: ACTIVITY_TYPES.CIT,
	setFilteredReceivables: () => {},
	isFunded: () => false,
	isUnfunded: () => false,
	isFundable: () => false,
	fundedSources: [],
});

interface ICitContextProviderProps {
	children: ReactNode;
	cit?: ICITBase;
	activityType?: string;
	receivables: ICitActivity[];
	exportHistory?: ICitExportHistoryEntries[] | null;
	citSettings?: Awaited<ReturnType<typeof getCITSystemSettings>>;
}

const CitContextProvider = ({ children, exportHistory = [], receivables, citSettings }: Readonly<ICitContextProviderProps>) => {
	const [filteredReceivables, setFilteredReceivables] = useState<string[] | null>(null);

	const fundedSources = useMemo(
		() => citSettings?.dmsSources?.filter((item) => DMS_FUNDED_SOURCES.includes(item.label))?.map((item) => item.value) ?? [],
		[citSettings]
	);

	const isFundable = useCallback(
		(activity: ICitActivity) => typeof activity?.funded === "boolean" && activity.activityType === ACTIVITY_TYPES.CIT,
		[]
	);
	const isFunded = useCallback((activity: ICitActivity) => isFundable(activity) && Boolean(activity?.funded), []);
	const isUnfunded = useCallback((activity: ICitActivity) => isFundable(activity) && !isFunded(activity), []);

	const value = useMemo(
		() => ({
			receivables,
			filteredReceivables,
			setFilteredReceivables,
			citSettings,
			isFunded,
			isUnfunded,
			isFundable,
			fundedSources,
			exportHistory,
		}),
		[receivables, filteredReceivables, setFilteredReceivables, citSettings, fundedSources]
	);

	return <CitContext.Provider value={value}>{children}</CitContext.Provider>;
};

// ICitContextValues
function useCitContextData() {
	const context = useContext(CitContext);
	if (!context) {
		throw new Error("useCitContextData must be used within a CitContextProvider");
	}
	return context;
}

export { CitContextProvider, useCitContextData };
